import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import arrowIcon from 'assets/images/icons/arrow-narrow-up-right.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import chevronIcon from 'assets/images/icons/chevron.svg';
import crossGreyIcon from 'assets/images/icons/cross-grey.svg';
import eyeOffGreenIcon from 'assets/images/icons/eye-off-green.svg';
import eyeOffGreyIcon from 'assets/images/icons/eye-off-grey.svg';
import fieldCollapseIcon from 'assets/images/icons/field-collapse.svg';
import messageQuestionCircleIcon from 'assets/images/icons/message-question-circle.svg';
import movieGreyIcon from 'assets/images/icons/movie-grey.svg';
import movieIcon from 'assets/images/icons/movie.svg';
import noteGreyIcon from 'assets/images/icons/note-grey.svg';
import noteIcon from 'assets/images/icons/note.svg';
import ticketIcon from 'assets/images/icons/ticket.svg';
import ticketGreyIcon from 'assets/images/icons/ticket-grey.svg';
import filePlusGreenIcon from 'assets/images/icons/file-plus-green.svg';
import photoGreyIcon from 'assets/images/icons/photo-grey.svg';
import photoIcon from 'assets/images/icons/photo.svg';
import pdfPageGreyIcon from 'assets/images/icons/pdf-page-grey.svg';
import pdfPageIcon from 'assets/images/icons/pdf-page.svg';
import trashIcon from 'assets/images/icons/trash-red-2.svg';
import pageIcon from 'assets/images/icons/page.svg';
import plusGreyIcon from 'assets/images/icons/plus-grey-2.svg';
import plusIcon from 'assets/images/icons/plus.svg';
import requiredIcon from 'assets/images/icons/required.svg';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import FieldDetailView from './FieldDetailView';
import NestedSelect from 'components/form-components/NestedSelect';
import SwappingComponent from './SwappingComponent';
import { isAllValues, userFieldList } from '../utils';
import { isRTL } from 'config/config';
import MultiCheckboxBridge from 'components/form-components/MultiCheckboxBridge';

const fieldTypeMap = Object.fromEntries([...userFieldList].map((f) => [f.elementType, f]));

const UserFieldComponent = ({
  field,
  hideAction,
  direction,
  depth,
  selectComponent,
  changeFormElementOrder,
  isOpened,
  isSelected,
  selectedField,
  dataSourcesList,
  companyFieldList,
  currentPage,
  totalPages,
  pagesList,
  pageElements,
  elementIndex,
  gotoPageTriggerMapObj,
  addTriggerInElement,
  handleTriggerElementChange,
  addTriggerType,
  removeTriggerType,
  removeAllRowLevelTriggers,
  setFieldDragged,
  checkSwapArrow,
  parentElementIndex,
  triggerRowIndex,
  showQuestionNumbering,
  getQuestionNumber,
}) => {
  const isLanRTL = isRTL();
  const { t } = useTranslation();
  const getDynamicStyle = (fieldName) => {
    const style = {
      color: field.properties?.basicProperties?.[`${fieldName}Color`],
      fontSize: field.properties?.basicProperties?.[`${fieldName}Size`],
    };
    if ((field.properties?.basicProperties?.[`${fieldName}FontType`] ?? []).includes('italic')) {
      style['fontStyle'] = 'italic';
    }
    if ((field.properties?.basicProperties?.[`${fieldName}FontType`] ?? []).includes('bold')) {
      style['fontWeight'] = 'bolder';
    }
    if ((field.properties?.basicProperties?.[`${fieldName}FontType`] ?? []).includes('underline')) {
      style['textDecoration'] = 'underline';
    }
    if (
      (field.properties?.basicProperties?.[`${fieldName}FontType`] ?? []).includes('strikethrough')
    ) {
      style['textDecoration'] = 'line-through';
    }
    if (
      (field.properties?.basicProperties?.[`${fieldName}FontType`] ?? []).includes('underline') &&
      (field.properties?.basicProperties?.[`${fieldName}FontType`] ?? []).includes('strikethrough')
    ) {
      style['textDecoration'] = 'underline line-through';
    }

    return style;
  };
  const currentPageElement = pagesList.find(({ pageNumber }) => pageNumber === currentPage).page;

  const checkIfMultiChoiceDisabled = () => {
    return (field.properties?.basicProperties?.multipleChoice ?? false) || options.length === 0;
  };

  const checkIfPageDisabled = (types, pageId) => {
    //checkIfPageMappedFromOtherSide
    //checkIfPageAlreadyMapped
    return (
      !!gotoPageTriggerMapObj[pageId]?.includes(
        currentPageElement.elementGlobalId ?? currentPageElement.triggerFlowReferenceId,
      ) ||
      types.find(
        ({ type, elementGlobalId, triggerFlowReferenceId }) =>
          type === 'user_page' && (pageId === elementGlobalId || pageId === triggerFlowReferenceId),
      )
    );
  };

  const getTriggerValues = (types = []) => {
    return [
      {
        value: 'ask_a_question',
        displayValue: 'Ask Question',
        disabled: depth > 2,
        icon: messageQuestionCircleIcon,
        nestedOptions: [...userFieldList]
          .filter((f) =>
            [
              'text',
              'number',
              'checkList',
              'image',
              'location',
              'dateTime',
              'signature',
              'ratingSlider',
              'numberSlider',
            ].includes(f.elementType),
          )
          .map((f) => ({ value: f.elementType, displayValue: f.displayName, icon: f.icon })),
        subOptionsArrowClass: 'invisible',
        subOptionsSelectClass: 'trigger_type',
      },
      { value: 'require_picture', displayValue: 'Require Picture', icon: photoIcon },
      { value: 'require_video', displayValue: 'Require Video', icon: movieIcon },
      { value: 'require_note', displayValue: 'Require Note', icon: filePlusGreenIcon },
      {
        value: 'go_to',
        displayValue: 'Go to',
        nestedOptions: pagesList
          .filter(({ pageNumber }) => pageNumber !== currentPage)
          .map(({ page }) => ({
            value: `user_page|${page.elementGlobalId ?? page.triggerFlowReferenceId}`,
            disabled: checkIfPageDisabled(
              types,
              page.elementGlobalId ?? page.triggerFlowReferenceId,
            ),
            displayValue:
              page.properties.basicProperties.pageTitle !== ''
                ? page.properties.basicProperties.pageTitle
                : t('form_untitled_page'),
          })),
      },
    ];
  };
  const pageTitleMap = Object.fromEntries(
    pagesList.map(({ page }) => [
      page.elementGlobalId ?? page.triggerFlowReferenceId,
      page.properties.basicProperties.pageTitle !== ''
        ? page.properties.basicProperties.pageTitle
        : t('form_untitled_page'),
    ]),
  );

  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [triggerDeletionProps, setTriggerDeletionProps] = useState([]);
  const [triggerRowLevelDeletionProps, setTriggerRowLevelDeletionProps] = useState([]);
  const [options, setOptions] = useState(
    (field.properties.basicProperties?.optionList ?? []).map((option) => ({
      value: option.value,
      displayValue: option.responseText,
    })),
  );

  const handleTriggerTypeChange = (name, value, triggerIndex) => {
    console.log(name, value, triggerIndex);
    if (name === 'ask_a_question') {
      addTriggerType('checkList', elementIndex, triggerIndex, name, value);
    } else if (name === 'go_to') {
      console.log('go_to', value);
      const [type, pageId] = value.split('|');
      const foundPage = pagesList.find(
        ({ page }) => page.elementGlobalId === pageId || page.triggerFlowReferenceId === pageId,
      );
      if (foundPage) {
        addTriggerType('checkList', elementIndex, triggerIndex, name, type, {
          elementGlobalId: foundPage.page.elementGlobalId,
          triggerFlowReferenceId: foundPage.page.triggerFlowReferenceId,
        });
      }
    } else {
      addTriggerType('checkList', elementIndex, triggerIndex, value);
    }
  };

  const getDisplayValue = (list, checkValue, defaultValue = '') => {
    return list.find((o) => o.value === checkValue)?.displayValue ?? defaultValue;
  };

  useEffect(() => {
    if (field.elementType === 'checkList' && field.isOpen) {
      setOptions(
        (field.properties.basicProperties?.optionList ?? []).map((option) => ({
          value: option.value,
          displayValue: option.responseText,
        })),
      );
    }
  }, [field]);

  return (
    <Fragment>
      <div
        className={`field-component-new${isSelected ? ' selected' : ''} ${
          isOpened ? ' opened' : ''
        }`}
      >
        <div
          className='field-component-header'
          draggable={!field.childReferenceId}
          onDragStart={() =>
            setFieldDragged({ fieldCategory: 'user-field', position: elementIndex })
          }
          onDragEnd={(e) => {
            if (e.dataTransfer.dropEffect === 'none') setFieldDragged(null);
          }}
        >
          <div className='d-flex gap-2 justify-content-between flex-grow-1'>
            <div
              className='d-flex align-items-center gap-2 flex-grow-1 cursor-pointer'
              onClick={() => selectComponent(elementIndex, field.elementType)}
            >
              {showQuestionNumbering && (
                <label className='border-grey-13-h-1 px-2 rounded-sm theme-size-0_9'>
                  {getQuestionNumber(field.elementGlobalId ?? field.uniqueIdToReplace)}
                </label>
              )}
              <img
                src={fieldCollapseIcon}
                className={`
                  ${isOpened ? '' : `rotate-${direction === 'rtl' ? '90' : '270'}`}
                  ${isSelected ? '' : 'svg-grey'}
                `}
              />
              <label className='cursor-pointer theme-size-0_9'>
                {field.properties?.basicProperties?.fieldTitle
                  ? field.properties?.basicProperties?.fieldTitle
                  : t('form_untitled_field')}
              </label>
              {/* <label className='theme-text-black-1 theme-size-0_9'>**Depth: {depth}**</label> */}
            </div>
            <div className='d-flex align-items-center gap-2 field-property-icons'>
              {field.properties?.validationProperties?.readOnly && (
                <label className='theme-size-0_8 text-nowrap'>Read Only</label>
              )}
              {field.properties?.ticketProperties?.ticketEnabled && (
                <img src={isSelected ? ticketIcon : ticketGreyIcon} />
              )}
              {field.properties?.validationProperties?.hidden && (
                <img src={isSelected ? eyeOffGreenIcon : eyeOffGreyIcon} />
              )}
              {field.properties?.mediaProperties?.allowVideos && (
                <img src={isSelected ? movieIcon : movieGreyIcon} />
              )}
              {field.properties?.mediaProperties?.allowNotes && (
                <img src={isSelected ? noteIcon : noteGreyIcon} />
              )}
              {field.properties?.mediaProperties?.allowPictures && (
                <img src={isSelected ? photoIcon : photoGreyIcon} />
              )}
              {field.properties?.mediaProperties?.allowDocuments && (
                <img src={isSelected ? pdfPageIcon : pdfPageGreyIcon} />
              )}
              {field.properties?.validationProperties?.required && <img src={requiredIcon} />}
            </div>
          </div>
          <div
            className={`d-flex gap-2 border-${direction === 'ltr' ? 'left' : 'right'}-grey-1-h-1`}
          >
            <div className={`if-icon${field.elementType === 'checkList' ? ' active' : ''}`}>
              <img src={field.elementType === 'checkList' ? plusIcon : plusGreyIcon} />
              <label>IF</label>
            </div>
            <div className='element-icon'>
              <img src={fieldTypeMap[field.elementType].icon} />
            </div>
            <div className='d-flex gap-2'>
              <SwappingComponent
                elementType={field.elementType}
                type='field'
                props={{ position: elementIndex }}
                changeFormElementOrder={changeFormElementOrder}
                isSelected={isSelected}
                hideAction={hideAction}
                {...checkSwapArrow(
                  elementIndex,
                  field.elementType,
                  'user-field',
                  parentElementIndex,
                  triggerRowIndex,
                )}
              />
            </div>
          </div>
        </div>
        <div className='field-component-body'>
          <p style={getDynamicStyle('fieldHintText')}>
            {field.properties?.basicProperties?.fieldHintText
              ? field.properties?.basicProperties?.fieldHintText
              : t('form_hint')}
          </p>
          <FieldDetailView
            element={field}
            dataSourcesList={dataSourcesList}
            companyFieldList={companyFieldList}
          />

          {field.elementType === 'checkList' && (
            <Fragment>
              {field.triggers?.map((trigger, triggerIndex) => (
                <div key={triggerIndex} className='d-flex gap-2 mx-1 mt-3'>
                  <div
                    className={`border-green-1-h-2 border-${
                      direction === 'rtl' ? 'start' : 'end'
                    }-0 px-1`}
                  ></div>
                  <div className='flex-grow-1 p-2'>
                    <div
                      className={`d-flex gap-2 flex-wrap align-items-center ${
                        (direction === 'ltr') === isLanRTL ? 'justify-content-end' : ''
                      }`}
                      style={{ direction: isLanRTL ? 'rtl' : 'ltr' }}
                    >
                      <img
                        className='cursor-pointer'
                        src={trashIcon}
                        onClick={() => {
                          setDeleteConfirmationModal(true);
                          setTriggerRowLevelDeletionProps([
                            'checkList',
                            elementIndex,
                            triggerIndex,
                          ]);
                        }}
                      />
                      <label className='theme-size-0_9'>{t('form_field_if_answer')}</label>
                      <NestedSelect
                        options={isAllValues}
                        handleChange={(name, value) =>
                          handleTriggerElementChange(
                            'checkList',
                            elementIndex,
                            triggerIndex,
                            name,
                            value,
                          )
                        }
                        name='matcher'
                        toggleClasses='px-1'
                        toggleComponent={
                          <>
                            <label className='theme-size-0_9 cursor-pointer'>
                              {t(getDisplayValue(isAllValues, trigger.matcher))}
                            </label>
                            <img src={chevronGreyIcon} className='rotate-180' />
                          </>
                        }
                      />
                      {trigger.matcher === 'is_one_of' || trigger.matcher === 'is_not_one_of' ? (
                        <MultiCheckboxBridge
                          name={`matchingValue-${triggerIndex}`}
                          options={options}
                          triggerIndex={triggerIndex}
                          triggers={field.triggers}
                          trigger={trigger}
                          handleChange={(_, value) => {
                            handleTriggerElementChange(
                              'checkList',
                              elementIndex,
                              triggerIndex,
                              'matchingValue',
                              value,
                            );
                          }}
                        />
                      ) : (
                        <NestedSelect
                          options={options.filter(
                            (o) =>
                              !field.triggers.find(
                                (t, i) =>
                                  i !== triggerIndex &&
                                  t.matcher === trigger.matcher &&
                                  t.matchingValue === o.value,
                              ),
                          )}
                          handleChange={(name, value) =>
                            handleTriggerElementChange(
                              'checkList',
                              elementIndex,
                              triggerIndex,
                              name,
                              value,
                            )
                          }
                          name='matchingValue'
                          toggleClasses='px-2 py-1 border-grey-1-h-1'
                          toggleComponent={
                            <>
                              <label className='theme-size-0_9 cursor-pointer'>
                                {t(getDisplayValue(options, trigger.matchingValue, 'select_value'))}
                              </label>
                              <img src={chevronGreyIcon} className='rotate-180' />
                            </>
                          }
                        />
                      )}
                      {trigger.matchingValue && (
                        <Fragment>
                          <label className='mx-2 theme-size-0_9'>{t('text_then')}</label>
                          {trigger.types.map(({ type, ...refData }, typeIndex) => (
                            <div
                              key={typeIndex}
                              className='d-flex gap-2 align-items-center theme-background-green-4 p-2 rounded-sm'
                            >
                              <img
                                className='cursor-pointer theme-height-20'
                                src={
                                  type === 'require_picture'
                                    ? photoIcon
                                    : type === 'require_video'
                                    ? movieIcon
                                    : type === 'require_note'
                                    ? filePlusGreenIcon
                                    : type === 'user_page'
                                    ? pageIcon
                                    : messageQuestionCircleIcon
                                }
                              />
                              {type === 'user_page' ? (
                                <>
                                  <label className='theme-text-green-1 theme-size-0_9'>GO TO</label>
                                  <img src={arrowIcon} />
                                  <label className='theme-text-green-1 theme-size-0_9'>
                                    {
                                      pageTitleMap[
                                        refData.elementGlobalId ?? refData.triggerFlowReferenceId
                                      ]
                                    }
                                  </label>
                                </>
                              ) : (
                                <label className='theme-text-green-1 theme-size-0_9'>
                                  {t('text_' + type)}
                                </label>
                              )}
                              <img
                                className='cursor-pointer'
                                onClick={() => {
                                  setDeleteConfirmationModal(true);
                                  setTriggerDeletionProps([
                                    'checkList',
                                    elementIndex,
                                    triggerIndex,
                                    typeIndex,
                                  ]);
                                }}
                                src={crossGreyIcon}
                              />
                            </div>
                          ))}

                          <NestedSelect
                            options={getTriggerValues(trigger.types)}
                            handleChange={(name, value) =>
                              handleTriggerTypeChange(name, value, triggerIndex)
                            }
                            name='type'
                            toggleClasses='px-1 py-2'
                            toggleComponent={
                              <>
                                <label className='theme-size-0_9 cursor-pointer theme-text-green-1'>
                                  {t('form_field_trigger')}
                                </label>
                                <img src={chevronIcon} className='rotate-180' />
                              </>
                            }
                          />
                        </Fragment>
                      )}
                    </div>

                    <div className='child-elements'>
                      {pageElements.map((childElement, index) => (
                        <Fragment key={index}>
                          {childElement.childReferenceId &&
                            trigger.types.find(
                              (type) => type.childReferenceId === childElement.childReferenceId,
                            ) && (
                              <div className='mt-3'>
                                <UserFieldComponent
                                  elementIndex={index}
                                  field={childElement}
                                  hideAction
                                  direction={direction}
                                  depth={depth + 1}
                                  selectComponent={selectComponent}
                                  changeFormElementOrder={changeFormElementOrder}
                                  isOpened={childElement.isOpen}
                                  isSelected={index === selectedField?.position}
                                  selectedField={selectedField}
                                  dataSourcesList={dataSourcesList}
                                  companyFieldList={[]}
                                  currentPage={currentPage}
                                  totalPages={totalPages}
                                  pagesList={pagesList}
                                  pageElements={pageElements}
                                  gotoPageTriggerMapObj={gotoPageTriggerMapObj}
                                  addTriggerInElement={addTriggerInElement}
                                  handleTriggerElementChange={handleTriggerElementChange}
                                  addTriggerType={addTriggerType}
                                  removeTriggerType={removeTriggerType}
                                  removeAllRowLevelTriggers={removeAllRowLevelTriggers}
                                  setFieldDragged={setFieldDragged}
                                  checkSwapArrow={checkSwapArrow}
                                  parentElementIndex={elementIndex}
                                  triggerRowIndex={triggerIndex}
                                  showQuestionNumbering={showQuestionNumbering}
                                  getQuestionNumber={getQuestionNumber}
                                />
                              </div>
                            )}
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {depth < 4 && (
                <div
                  className={`if${checkIfMultiChoiceDisabled() ? ' disabled' : ''}`}
                  onClick={() =>
                    checkIfMultiChoiceDisabled()
                      ? null
                      : addTriggerInElement('checkList', elementIndex)
                  }
                >
                  <img src={checkIfMultiChoiceDisabled() ? plusGreyIcon : plusIcon} />
                  <label className='theme-text-green-1'>IF</label>
                  <label></label>
                </div>
              )}
            </Fragment>
          )}
        </div>
      </div>
      <ConfirmationModal
        show={deleteConfirmationModal}
        title={t('alert_form_element_deletion_title')}
        message={t(
          triggerDeletionProps.length
            ? 'alert_trigger_deletion_message'
            : triggerRowLevelDeletionProps.length
            ? 'alert_all_row_triggers_deletion_message'
            : '',
        )}
        actionList={[
          {
            color: 'black-1',
            text: t('button_cancel'),
            onClick: () => {
              setTriggerDeletionProps([]);
              setTriggerRowLevelDeletionProps([]);
              setDeleteConfirmationModal(false);
            },
          },
          {
            color: 'red-1',
            text: t('button_delete'),
            onClick: () => {
              if (triggerDeletionProps.length) {
                removeTriggerType(...triggerDeletionProps);
                setTriggerDeletionProps([]);
              } else if (triggerRowLevelDeletionProps.length) {
                removeAllRowLevelTriggers(...triggerRowLevelDeletionProps);
                setTriggerRowLevelDeletionProps([]);
              }
              setDeleteConfirmationModal(false);
            },
          },
        ]}
      />
    </Fragment>
  );
};

UserFieldComponent.propTypes = {
  field: PropTypes.object.isRequired,
  hideAction: PropTypes.bool,
  direction: PropTypes.string.isRequired,
  depth: PropTypes.number.isRequired,
  selectComponent: PropTypes.func.isRequired,
  changeFormElementOrder: PropTypes.func.isRequired,
  isOpened: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  selectedField: PropTypes.object,
  dataSourcesList: PropTypes.array.isRequired,
  companyFieldList: PropTypes.array.isRequired,
  elementIndex: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  pagesList: PropTypes.array.isRequired,
  pageElements: PropTypes.array.isRequired,
  gotoPageTriggerMapObj: PropTypes.object.isRequired,
  addTriggerInElement: PropTypes.func.isRequired,
  handleTriggerElementChange: PropTypes.func.isRequired,
  addTriggerType: PropTypes.func.isRequired,
  removeTriggerType: PropTypes.func.isRequired,
  removeAllRowLevelTriggers: PropTypes.func.isRequired,
  setFieldDragged: PropTypes.func.isRequired,
  checkSwapArrow: PropTypes.func.isRequired,
  parentElementIndex: PropTypes.number,
  triggerRowIndex: PropTypes.number,
  showQuestionNumbering: PropTypes.bool.isRequired,
  getQuestionNumber: PropTypes.func.isRequired,
};

export default UserFieldComponent;
